import React from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: React.ReactNode;
  /** Optional reset key — when this changes, the boundary clears. Used at the
   *  route level to recover automatically as the doctor navigates away. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors so a thrown component doesn't unmount the entire
 * React tree to a blank screen. Pre-fix, any uncaught error on the
 * encounter review (or any other page) wiped App-level chrome — dock,
 * launcher pill, feedback widget — and the doctor was stranded with no
 * way to restart or report what happened.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log goes through the buffered console logger so the FeedbackWidget
    // can attach it to a bug report.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught', error, info.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private handleReset = () => {
    this.setState({ error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const message = this.state.error.message || 'Unknown error';
    return (
      <div
        className="min-h-[50vh] flex flex-col items-center justify-center px-4 py-10 text-center"
        style={{ color: 'var(--color-text-primary)' }}
      >
        <h1 className="text-lg font-semibold mb-2">Something went wrong.</h1>
        <p
          className="text-sm max-w-md mb-1"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          The page hit an unexpected error. Your work in progress is safe.
        </p>
        <pre
          className="text-xs max-w-md whitespace-pre-wrap break-words rounded-md px-3 py-2 mb-4 mt-3 text-start"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {message}
        </pre>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={this.handleReset}>
            Try again
          </Button>
          <Button onClick={this.handleReload}>Reload</Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
